import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useNavigate, useLocation } from 'react-router-dom';
import PdfPreview from '../../../../Common/PDFPreview';
import { RingLoader } from 'react-spinners';
import { Typography } from '@mui/material';
import { FormaDateBalanceSheet } from "../../../../Common/FormatFunctions/FormatDate"
import PrintButton from '../../../../Common/Buttons/PrintPDF';

const apikey = process.env.REACT_APP_API;

const TransportAcRegister = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search);
    const fromDate = searchParams.get('fromDate');
    const toDate = searchParams.get('toDate');
    const acCode = searchParams.get('acCode');
    const companyCode = sessionStorage.getItem('Company_Code');
    const Year_Code = sessionStorage.getItem('Year_Code');
    const Company_Name = sessionStorage.getItem('Company_Name');
    const Company_GSTNO = sessionStorage.getItem('Company_GSTNO')

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pdfPreview, setPdfPreview] = useState(null);

    const API_URL = `${apikey}/TransportAc-Register`;

    useEffect(() => {
        const fetchReportData = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await axios.get(API_URL, {
                    params: {
                        fromDT: fromDate,
                        toDT: toDate,
                        Company_Code: companyCode,
                        Year_Code: Year_Code,
                        acCode: acCode
                    },
                });
                setReportData(response.data);
            } catch (error) {
                console.error('Error fetching report:', error);
                setError('Error fetching report');
            } finally {
                setLoading(false);
            }
        };

        if (fromDate && toDate) {
            fetchReportData();
        }
    }, [fromDate, toDate, companyCode, Year_Code]);

    const handlePrint = () => {
        const printContent = document.getElementById('reportTable').outerHTML;
        const win = window.open('', '', 'height=700,width=900');
        win.document.write(`
            <html>
                <head>
                    <title>Print Report</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            margin: 20px;
                        }
                        .company-name {
                            text-align: center;
                            font-size: 24px;
                            font-weight: bold;
                            margin-bottom: 20px;
                            color: #333;
                        }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-bottom: 20px;
                        }
                        th, td {
                            border: 1px solid #ddd;
                            padding: 8px;
                            text-align: left;
                        }
                        th {
                            background-color: #f2f2f2;
                            font-weight: bold;
                        }
                        tr:nth-child(even) {
                            background-color: #f9f9f9;
                        }
                        .total-row {
                            background-color: #e0f7fa;
                            font-weight: bold;
                        }
                    </style>
                </head>
                <body>
                    <div class="company-name">${Company_Name}</div>
                    ${printContent}
                </body>
            </html>
        `);
        win.document.close();
        win.print();
    };

    const generatePdf = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const textWidth = doc.getTextWidth(Company_Name);
        const xPosition = (pageWidth - textWidth) / 2;

        doc.text(Company_Name, xPosition, 10);
        doc.autoTable({ html: '#reportTable' });

        const pdfBlob = doc.output('blob');
        const url = URL.createObjectURL(pdfBlob);
        setPdfPreview(url);
    };

    const handleExportToExcel = () => {
        const wb = XLSX.utils.book_new();
        const wsData = [];

        // Add company name and report header
        wsData.push([Company_Name]);
        wsData.push(['Transport Account Register']);
        wsData.push([`From: ${fromDate} To: ${toDate}`]);
        wsData.push([]);

        // Table headers (same as your HTML table)
        wsData.push([
            'Memo#', 'Date', 'Party Name', 'Mill', 'Truck No',
            'Qntl', 'Rate', 'Freight', 'Paid1 + Paid2 + Paid3', 'Balance'
        ]);

        Object.entries(groupedReportData).forEach(([key, { items }]) => {
            // Group title row
            wsData.push([key]);

            let totalBalance = 0;

            items.forEach(item => {
                const freight = parseFloat(item.Freight) || 0;
                const paid1 = parseFloat(item.Paid1) || 0;
                const paid2 = parseFloat(item.Paid2) || 0;
                const paid3 = parseFloat(item.Paid3) || 0;
                const balance = freight - (paid1 + paid2 + paid3);
                totalBalance += balance;

                wsData.push([
                    item.doc_no,
                    item.dt,
                    item.VoucherBy,
                    item.MillShort,
                    item.lorry,
                    item.Qntl,
                    item.Rate,
                    item.Freight,
                    `${item.Paid1} + ${item.Paid2} + ${item.Paid3}`,
                    balance.toFixed(2)
                ]);
            });

            // Total row for the group
            wsData.push(['', '', '', '', '', '', '', '', 'Total', totalBalance.toFixed(2)]);
            wsData.push([]); // Spacer
        });

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, 'TransportAcRegister');
        XLSX.writeFile(wb, 'TransportAcRegister.xlsx');
    };



    const groupReportData = (data) => {
        if (!Array.isArray(data)) {
            console.error("Expected data to be an array, but got:", data);
            return {}; // Return an empty object if data is not an array
        }

        const grouped = {};
        data.forEach(item => {
            const key = `${item.transport}-${item.transportname}`;
            if (!grouped[key]) {
                grouped[key] = { items: [] };
            }
            grouped[key].items.push(item);
        });
        return grouped;
    };


    const groupedReportData = groupReportData(reportData);

    return (
        <div style={{ marginTop: '-80px' }}>
            {/* <Typography variant="h6" style={{ textAlign: 'center', fontSize: "24px", fontWeight: "bold" }}>{Company_Name}</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px", textDecoration: 'underline', fontWeight: "550" }}>GSTN : {Company_GSTNO}</Typography> */}

            <div className="d-flex justify-content-between align-items-center">
                <div style={{ flex: 1, textAlign: 'center', marginLeft: "280px" }}>
                    <Typography variant="h6" style={{ textAlign: 'center', fontSize: "20px", fontWeight: "bold" }}>Transport Account</Typography>
                    <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px" }}>{FormaDateBalanceSheet(fromDate)} to {FormaDateBalanceSheet(toDate)}</Typography>
                </div>

                <div className="d-flex justify-content-end">
                    <PrintButton disabledFeild={""} fetchData={handlePrint} />
                    <button className="btn btn-success me-2" onClick={handleExportToExcel}>Export to Excel</button>
                </div>
            </div>

            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                    <RingLoader />
                </div>
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : (
                <div style={{ maxHeight: "800px", overflowY: "auto" }}>
                    <table className="table table-striped table-bordered mt-4" id="reportTable" style={{ marginBottom: "60px", width: "100%" }}>
                        <thead className="table-light" style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1, whiteSpace: "nowrap" }} >
                            <tr>
                                <th>Memo#</th>
                                <th>Date</th>
                                <th>Party Name</th>
                                <th>Mill Name</th>
                                <th>truck No</th>
                                <th>Quintal</th>
                                <th>Rate</th>
                                <th>Frieght</th>
                                <th>Piad</th>
                                <th>Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(groupedReportData).map(([key, { items }]) => {
                                const totalQty = items.reduce((sum, item) =>
                                    sum + (parseFloat(item.Freight) - (parseFloat(item.Paid1) + parseFloat(item.Paid2) + parseFloat(item.Paid3)))
                                    , 0);
                                return (
                                    <React.Fragment key={key}>
                                        <tr>
                                            <td colSpan={10} align="left" className="table-primary" style={{ color: 'blue', fontWeight: 'bold' }}>{key}</td>
                                        </tr>
                                        {items.map((item, index) => (
                                            <tr key={index}>
                                                <td style={{ fontWeight: 'bold' }} align="center">{item.doc_no}</td>
                                                <td align="center">{item.dt}</td>
                                                <td align="left">{item.VoucherBy}</td>
                                                <td align="left">{item.MillShort}</td>
                                                <td align="left">{item.lorry}</td>
                                                <td align="right">{item.Qntl}</td>
                                                <td align="right">{item.Rate}</td>
                                                <td align="right">{item.Freight}</td>
                                                <td align="right">{item.Paid1}+{item.Paid2}+{item.Paid3}</td>
                                                <td align="right"> {parseFloat(item.Freight) - (parseFloat(item.Paid1) + parseFloat(item.Paid2) + parseFloat(item.Paid3))}</td>

                                            </tr>
                                        ))}
                                        <tr style={{ fontWeight: 'bold', backgroundColor: '#f0f8ff' }}>
                                            <td colSpan={9} align="right">Total </td>
                                            <td>{totalQty.toFixed(2)}</td>

                                        </tr>
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {pdfPreview && (
                <div className="centered-container">
                    <PdfPreview pdfData={pdfPreview} apiData={reportData} label={'NewDispatchRegister'} />
                </div>
            )}
        </div>
    );
};

export default TransportAcRegister;